/* eslint-disable import/prefer-default-export */
import { isObject } from "common/utils/core";

const APP_MONITORING_KEY = "__APP_MONITORING__";
const DATADOG_SHIM_WRAPPED_KEY = "datadogShimWrapped";
const JIFFY_QUEUE_STUB_KEY = "__jiffyNotifyQueueStub";
const PENDING_NOTIFIES_KEY = "pendingHoneybadgerNotifies";
const NOTIFY_ERROR_COUNT_KEY = "notifyErrorCount";
const DEFAULT_MAX_ERRORS_PER_PAGE = 20;

const getFlags = () => {
  const flags = window[APP_MONITORING_KEY]?.flags;
  return {
    datadogEnabled: flags?.datadogEnabled !== false
  };
};

const getMaxErrorsPerPage = () => {
  const raw = window[APP_MONITORING_KEY]?.flags?.maxErrorsPerPage;
  if (raw === undefined || raw === null) return DEFAULT_MAX_ERRORS_PER_PAGE;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return DEFAULT_MAX_ERRORS_PER_PAGE;
  return n;
};

const reportToDatadog = (messageOrError, options = {}) => {
  try {
    if (!window.DD_RUM || typeof window.DD_RUM.addError !== "function") return;

    const mon = window[APP_MONITORING_KEY];
    if (mon) {
      const maxErrors = getMaxErrorsPerPage();
      const count = mon[NOTIFY_ERROR_COUNT_KEY] || 0;
      if (count >= maxErrors) return;
      mon[NOTIFY_ERROR_COUNT_KEY] = count + 1;
    }

    let callSiteContext = {};
    if (isObject(options)) {
      callSiteContext = isObject(options.context) ? options.context : options;
    }

    const globalContext = window[APP_MONITORING_KEY]?.context || {};
    const globalUser = window[APP_MONITORING_KEY]?.user || {};
    const mergedContext = { ...globalContext, ...globalUser, ...callSiteContext };

    if (messageOrError instanceof Error) {
      window.DD_RUM.addError(messageOrError, mergedContext);
    } else {
      const error = new Error(messageOrError);
      error.name = "CustomError";
      window.DD_RUM.addError(error, mergedContext);
    }
  } catch (_e) {
    // Observability must never crash the app.
  }
};

const flushPendingHoneybadgerNotifies = () => {
  try {
    const pending = window[APP_MONITORING_KEY]?.[PENDING_NOTIFIES_KEY];
    if (!Array.isArray(pending) || pending.length === 0) return;

    const batch = pending.splice(0, pending.length);
    batch.forEach(([messageOrError, options]) => {
      reportToDatadog(messageOrError, options);
    });
  } catch (_e) {
    // Observability must never crash the app.
  }
};

const replaceQueueStubWithShim = legacyClient => {
  window.Honeybadger = {
    notify: (messageOrError, options = {}) => {
      reportToDatadog(messageOrError, options);
    },
    setContext:
      typeof legacyClient.setContext === "function"
        ? legacyClient.setContext.bind(legacyClient)
        : () => {},
    [DATADOG_SHIM_WRAPPED_KEY]: true
  };
  flushPendingHoneybadgerNotifies();
};

const wrapLegacyNotifyClient = legacyClient => {
  const originalNotify = legacyClient.notify.bind(legacyClient);
  // Honeybadger client is a global singleton we patch in place.
  // eslint-disable-next-line no-param-reassign -- replace notify on the legacy client
  legacyClient.notify = (messageOrError, options = {}) => {
    let result;
    try {
      result = originalNotify(messageOrError, options);
    } catch (_e) {
      // Observability must never crash the app.
    }
    reportToDatadog(messageOrError, options);
    return result;
  };
  // eslint-disable-next-line no-param-reassign -- mark client as wrapped for idempotent install
  legacyClient[DATADOG_SHIM_WRAPPED_KEY] = true;
  flushPendingHoneybadgerNotifies();
};

export const installBrowserNotifyShim = () => {
  const { datadogEnabled } = getFlags();
  if (!datadogEnabled) return;

  const legacyClient = window.Honeybadger;
  if (legacyClient && typeof legacyClient.notify === "function") {
    if (legacyClient[DATADOG_SHIM_WRAPPED_KEY] === true) return;

    if (legacyClient[JIFFY_QUEUE_STUB_KEY] === true) {
      replaceQueueStubWithShim(legacyClient);
      return;
    }

    wrapLegacyNotifyClient(legacyClient);
    return;
  }

  window.Honeybadger = {
    notify: (messageOrError, options = {}) => {
      reportToDatadog(messageOrError, options);
    },
    [DATADOG_SHIM_WRAPPED_KEY]: true
  };
  flushPendingHoneybadgerNotifies();
};
